import { setContext } from '@apollo/client/link/context/index.js'
import { getGarySessionCookieHeader } from '../../headers.ts'
import type { GaryClientOptions } from '../index.ts'

/**
 * Filters cookies to extract only the Gary session cookie.
 * Uses sessionCookieKey to determine which cookie to extract.
 * @param cookieString - The full cookie string
 * @param sessionCookieKey - The key for the Gary session cookie
 * @param logger - Optional logger instance
 * @returns Filtered cookie string containing only the Gary session cookie, or null if not found
 */

const filterGarySessionCookies = (
  cookieString: string | null,
  sessionCookieKey: string | undefined,
  logger: GaryClientOptions['logger'],
) => {
  if (!cookieString) return null

  // If no sessionCookieKey provided, pass through all cookies
  if (!sessionCookieKey) {
    if (logger) {
      logger.warn('Gary session cookie key not provided, passing all cookies may cause "cookies too large" errors')
    } else {
      console.warn('Gary session cookie key not provided, passing all cookies may cause "cookies too large" errors')
    }
    return cookieString
  }

  return getGarySessionCookieHeader({
    cookie: cookieString,
    sessionCookieKey,
  })
}

export const authLink = (
  getToken: GaryClientOptions['getToken'],
  getCookie: GaryClientOptions['getCookie'],
  sessionCookieKey: GaryClientOptions['sessionCookieKey'],
  logger: GaryClientOptions['logger'],
) => {
  return setContext(async (_, { headers }) => {
    const token = await getToken?.()
    const fullCookieString = await getCookie?.()

    // Filter cookies to only include Gary session cookies
    const garySessionCookie = filterGarySessionCookies(fullCookieString ?? null, sessionCookieKey, logger)

    return {
      headers: {
        ...headers,
        Authorization: `Bearer ${token}`,
        Cookie: garySessionCookie,
      },
    }
  })
}
